/**
 * NASA FIRMS fire detection data source.
 * Fetches active fire data from the Fire Information for Resource Management System.
 * Uses FIRMS MAP_KEY if available, falls back to public CSV endpoint.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'firms';

function frpToSeverity(frp: number): Severity {
  if (frp >= 100) return 'critical';
  if (frp >= 50) return 'high';
  if (frp >= 20) return 'medium';
  return 'low';
}

/** Split a CSV line handling double-quoted fields that may contain commas. */
function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'; // escaped quote
          i++;
        } else {
          inQuotes = false; // closing quote
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

/** Parse CSV text into an array of row objects keyed by header names. */
function parseCsv(text: string): Record<string, string>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];

  const headers = splitCsvLine(lines[0]).map(h => h.toLowerCase());
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = splitCsvLine(lines[i]);
    if (values.length < headers.length) continue;
    const row: Record<string, string> = {};
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = values[j] ?? '';
    }
    rows.push(row);
  }
  return rows;
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

export const firmsSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'NASA FIRMS',
    description: 'Fire Information for Resource Management System — satellite fire detection',
    icon: 'flame',
    category: 'environmental',
    updateInterval: 3_600_000, // 1 hour
    requiresApiKey: false, // works without key via public endpoint
    dataLicense: 'Public Domain',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [
    {
      key: 'firms_map_key',
      label: 'FIRMS MAP Key',
      type: 'password',
      required: false,
      helpText: 'Optional — enables the faster keyed API. Get one at https://firms.modaps.eosdis.nasa.gov/map/#d:24hrs;@0.0,0.0,3z',
    },
  ] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] FIRMS fire source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Proxy handles MAP_KEY injection and endpoint selection
      const url = `${getApiBase()}/api/data/firms`;

      const response = await fetch(url, { signal: AbortSignal.timeout(30_000) });

      if (!response.ok) {
        throw new Error(`FIRMS API returned ${response.status}`);
      }

      const text = await response.text();
      const rows = parseCsv(text);

      const events: NormalizedEvent[] = [];
      const seen = new Set<string>();
      // Cap fire events to avoid flooding the store (full CSV can have 50k+ rows)
      const MAX_FIRE_EVENTS = 8_000;

      for (const row of rows) {
        if (events.length >= MAX_FIRE_EVENTS) break;
        const lat = parseFloat(row['latitude']);
        const lng = parseFloat(row['longitude']);
        const frp = parseFloat(row['frp'] ?? '0');
        const confidence = row['confidence'] ?? 'nominal';
        const acqDate = row['acq_date'] ?? '';
        const acqTime = row['acq_time'] ?? '';

        if (isNaN(lat) || isNaN(lng)) continue;

        // Deduplicate by position + time
        const dedupeKey = `${lat.toFixed(3)}-${lng.toFixed(3)}-${acqDate}-${acqTime}`;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);

        const timestamp = acqDate && acqTime
          ? new Date(`${acqDate}T${acqTime.padStart(4, '0').slice(0, 2)}:${acqTime.padStart(4, '0').slice(2)}:00Z`).toISOString()
          : new Date().toISOString();

        events.push({
          id: `firms-${dedupeKey}`,
          source: SOURCE_ID,
          timestamp,
          type: 'fire',
          title: `Fire detected — ${isNaN(frp) ? 'unknown' : frp.toFixed(0)} FRP`,
          severity: frpToSeverity(isNaN(frp) ? 0 : frp),
          location: { lat, lng },
          tags: ['fire', 'satellite', confidence],
          raw: {
            frp,
            confidence,
            brightness: parseFloat(row['brightness'] ?? '0'),
            satellite: row['satellite'] ?? '',
            daynight: row['daynight'] ?? '',
          },
        });
      }

      status.state = 'active';
      status.lastFetch = new Date().toISOString();
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
